import React, { useEffect, useState } from "react";
import PptxGenJS from "pptxgenjs";
import { getEmployeePrompts } from "api/employeePromptApi";
import { Button } from "antd";

export const BioSlide = ({ employee = {} }) => {
  const BASE_URL =
    process.env.REACT_APP_UI_URL || window.location.origin;
  const currentDate = new Date();
  const today =
    currentDate.getFullYear() +
    '-' +
    String(currentDate.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(currentDate.getDate()).padStart(2, '0');

  let prefix = '';
  if (BASE_URL.includes('localhost')) {
    prefix = 'LOC';
  } else if (BASE_URL.includes('dev')) {
    prefix = 'DEV';
  }

  const [prompts, setPrompts] = useState([]);
  const [experiencedRoles, setExperiencedRoles] = useState([]);
  const [skills, setSkills] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [practiceMajor, setPracticeMajor] = useState([]);
  const profileLink = `${BASE_URL}/employees/${employee.id}`;
  const formatCommaText = (arr) => {
    if (!arr || !arr.length) return "None";
    return arr.join(", ");
  };

  const formatSkillsCommaText = (input) => {
    if (!input) return "None";

    if (Array.isArray(input)) {
      return input.join(", ");
    }

    if (typeof input === "object" && Array.isArray(input.skills)) {
      return input.skills.join(", ");
    }

    if (typeof input === "object" && typeof input.skills === "string") {
      return input.skills.replace(/,\s*/g, ", ");
    }

    return "None";
  };

  const formatCertsBarSeparator = (input) => {
    if (!input) return "None";

    if (Array.isArray(input)) {
      return input.join(" || ");
    }

    if (typeof input === "object" && Array.isArray(input.skills)) {
      return input.skills.join(" || ");
    }

    return "None";
  };


  const formatBioWithLink = (text) => {
    if (!text) return [{ text: "No response" }];

    const BIO_MAX_CHARS = 500;
    const bioIsLong = text.length > BIO_MAX_CHARS;

    const displayText = bioIsLong ? text.substring(0, BIO_MAX_CHARS) : text;

    const content = [
      {
        text: displayText + " ",
        options: { fontSize: 10 }
      }
    ];

    if (bioIsLong) {
      content.push({
        text: "\n\nMore >>",
        options: {
          fontSize: 10,
          color: "0563C1",
          underline: true,
          hyperlink: {
            url: profileLink
          }
        }
      });
    }

    return content;
  };

  const formatSkillsWithLink = (text) => {
    if (!text) return [{ text: "No response" }];

    const SKILLS_MAX_CHARS = 530;


    const formattedText = formatSkillsCommaText(text);
    if (formattedText === "None") {
      return [{ text: "No response" }];
    }


    const skillsIsLong = formattedText.length > SKILLS_MAX_CHARS;
    const displayText = skillsIsLong
      ? formattedText.substring(0, SKILLS_MAX_CHARS)
      : formattedText;

    const content = [
      {
        text: displayText + " ",
        options: { fontSize: 10 }
      }
    ];

    if (skillsIsLong) {
      content.push({
        text: "\n\nMore >>",
        options: {
          fontSize: 10,
          color: "0563C1",
          underline: true,
          hyperlink: {
            url: profileLink
          }
        }
      });
    }

    return content;
  };

  const formatStrengthsWithLink = (text) => {
    if (!text) return [{ text: "No response" }];

    const STRENGTHS_MAX_CHARS = 450;
    const strengthIsLong = text.length > STRENGTHS_MAX_CHARS;

    const displayText = strengthIsLong ? text.substring(0, STRENGTHS_MAX_CHARS) : text;

    const content = [
      {
        text: displayText + " ",
        options: { fontSize: 10 }
      }
    ];

    if (strengthIsLong) {
      content.push({
        text: "\n\nMore >>",
        options: {
          fontSize: 10,
          color: "0563C1",
          underline: true,
          hyperlink: {
            url: profileLink
          }
        }
      });
    }

    return content;
  };


  const formatCertsWithLink = (text) => {
    if (!text) return [{ text: "No response" }];

    const CERTS_MAX_CHARS = 348;


    const formattedText = formatCertsBarSeparator(text);
    if (formattedText === "None") {
      return [{ text: "No response" }];
    }


    const certsIsLong = formattedText.length > CERTS_MAX_CHARS;
    const displayText = certsIsLong
      ? formattedText.substring(0, CERTS_MAX_CHARS)
      : formattedText;

    const content = [
      {
        text: displayText + " ",
        options: { fontSize: 10 }
      }
    ];

    if (certsIsLong) {
      content.push({
        text: "\n\nMore >>",
        options: {
          fontSize: 10,
          color: "0563C1",
          underline: true,
          hyperlink: {
            url: profileLink
          }
        }
      });
    }

    return content;
  };


  useEffect(() => {
    if (!employee.id) return;

    getEmployeePrompts(employee.id).then(resp => {
      setPrompts(resp);
    });

    const roles = employee.roleTypes || [];
    setExperiencedRoles(
      roles
        .filter(r => r.experienceLevel !== "DESIRE_TO_FILL")
        .map(r => r.roleType.family + " - " + r.roleType.name)
    );

    const employeeSkills = employee.skills || [];
    setSkills(
      employeeSkills
        .filter(s => s.experienceLevel !== "DESIRE_TO_LEARN")
        .map(s => s.skill.name)
    );

    const employeeCertifications = employee.certifications || [];
    setCertifications(
      employeeCertifications.length
        ? employeeCertifications.map(c => {
          let certificationDetails = "";

          if (c.certification && c.certification.name) {
            certificationDetails += c.certification.name;
          }
          if (c.certification && c.certification.certifyingOrganization) {
            certificationDetails +=
              " - " + c.certification.certifyingOrganization.name;
          }
          if (c.expirationDate) {
            certificationDetails += " - " + c.expirationDate;
          }
          return certificationDetails;
        })
        : ["None"]
    );
    if (
      employee.major &&
      employee.major.isCapability === false &&
      employee.major.industry
    ) {
      const industryName = employee.major.industry.name;
      const sectorImageMap = {
        Consumer: "../images/bio-slide/Consumer.png",
        "Energy & Utilities": "../images/bio-slide/Energy_Utilities.png",
        "Financial Services":
          "../images/bio-slide/Financial_Services_Insurance.png",
        "Healthcare & Life Sciences":
          "../images/bio-slide/Healthcare_Life_Sciences.png",
        "Public Sector": "../images/bio-slide/Public_Sector.png",
        "Technology, Media & Telecom":
          "../images/bio-slide/Technology_Media_Telecom.png"
      };
      const imagePaths = [];
      if (sectorImageMap[industryName]) {
        imagePaths.push(sectorImageMap[industryName]);
      }
    }
    setPracticeMajor(
      employee.practice && employee.majorCapability?.capability?.name
        ? `${employee.practice} - ${employee.majorCapability.capability.name}`
        : employee.practice ||
        employee.majorCapability?.capability?.name ||
        "No practice or major capability"
    );
  }, [employee]);

  const handleDownload = () => {
    const pptx = new PptxGenJS();
    pptx.layout = "LAYOUT_WIDE";

    pptx.defineSlideMaster({
      title: "PLACEHOLDER_SLIDE",
      background: { color: "ffffff" },
      objects: [
        { rect: { x: 0, y: 0, w: 3.2, h: 7.5, fill: { color: "ff3b1f" } } },
        {
          image: {
            x: 0.2,
            y: 7,
            w: 1.2,
            h: 0.2,
            path: "../images/bio-slide/Credera_Logo.png"
          }
        },
        {
          placeholder: {
            options: {
              name: "name",
              x: 0.6,
              y: 0.5,
              w: 2.8,
              h: 1,
              fontSize: 26,
              color: "ffffff",
              bold: true,
              wrap: true
            }
          }
        },
        {
          placeholder: {
            options: {
              name: "position",
              x: 0.6,
              y: 1.8,
              w: 2.5,
              h: 0.3,
              fontSize: 11,
              color: "ffffff",
              fontFace: "Lato Regular (Body)"
            },
            text: "position"
          }
        },
        {
          placeholder: {
            options: {
              name: "practice-major",
              x: 0.6,
              y: 2.1,
              w: 2.5,
              h: 0.3,
              fontSize: 11,
              color: "ffffff",
              fontFace: "Lato Regular (Body)"
            },
            text: "practice-major"
          }
        },
        {
          placeholder: {
            options: {
              name: "office",
              x: 0.6,
              y: 2.4,
              w: 2.5,
              h: 0.3,
              fontSize: 11,
              color: "ffffff",
              fontFace: "Lato Regular (Body)"
            },
            text: "office"
          }
        },
        {
          placeholder: {
            options: {
              name: "strengths",
              x: 0.6,
              y: 3.3,
              w: 2.5,
              h: 2,
              fontSize: 10,
              color: "ffffff",
              fontFace: "Lato Regular (Body)"
            },
            text: "key strengths"
          }
        },

        {
          placeholder: {
            options: {
              name: "bio",
              x: 4.5,
              y: 0.5,
              w: 7.5,
              h: 3.2,
              fontSize: 10,
              color: "3a3a3a",
              fontFace: "Lato Regular (Body)"
            },
            text: "bio"
          }
        },
        {
          placeholder: {
            options: {
              name: "role type",
              x: 4.5,
              y: 3.0,
              w: 7.5,
              h: 0.8,
              fontSize: 10,
              color: "3a3a3a",
              fontFace: "Lato Regular (Body)"
            },
            text: "role type"
          }
        },
        {
          placeholder: {
            options: {
              name: "skills",
              x: 4.5,
              y: 4.1,
              w: 7.5,
              h: 1.6,
              fontSize: 10,
              color: "3a3a3a",
              fontFace: "Lato Regular (Body)"
            },
            text: "skills"
          }
        },
        {
          placeholder: {
            options: {
              name: "certifications",
              x: 4.5,
              y: 5.8,
              w: 7.5,
              h: 1,
              fontSize: 10,
              color: "3a3a3a",
              fontFace: "Lato Regular (Body)"
            },
            text: "certifications"
          }
        }
      ]
    });

    const slide = pptx.addSlide({ masterName: "PLACEHOLDER_SLIDE" });

    //add icons to slide
    slide.addImage({
      path: "../images/bio-slide/Employee_Icon.png",
      x: 0.05,
      y: 1.75,
      w: 0.4,
      h: 0.4
    });
    slide.addImage({
      path: "../images/bio-slide/Bio_Icon.png",
      x: 4,
      y: 0.27,
      w: 0.4,
      h: 0.4
    });
    slide.addImage({
      path: "../images/bio-slide/Roles_Icon.png",
      x: 4,
      y: 2.85,
      w: 0.4,
      h: 0.4
    });
    slide.addImage({
      path: "../images/bio-slide/Skills_Icon.png",
      x: 4,
      y: 3.95,
      w: 0.4,
      h: 0.4
    });
    slide.addImage({
      path: "../images/bio-slide/Certification_Icon.png",
      x: 4,
      y: 5.65,
      w: 0.4,
      h: 0.4
    });

    //add headers to slide
    slide.addText("KEY STRENGTHS", {
      x: 0.6,
      y: 3,
      w: 2,
      h: 0.25,
      fontSize: 11,
      color: "d7ecf3",
      fontFace: "Lato Bold"
    });
    slide.addText("BIO", {
      x: 4.5,
      y: 0.3,
      fontSize: 11,
      color: "ff3b1f",
      bold: true
    });
    slide.addText("ROLE TYPES", {
      x: 4.5,
      y: 2.9,
      fontSize: 11,
      color: "ff3b1f",
      bold: true
    });

    slide.addText("SKILLS", {
      x: 4.5,
      y: 4.0,
      fontSize: 11,
      color: "ff3b1f",
      bold: true
    });

    slide.addText("EXTERNAL CERTIFICATIONS", {
      x: 4.5,
      y: 5.7,
      fontSize: 11,
      color: "ff3b1f",
      bold: true
    });

    // DATA
    slide.addText(employee.fullName || "", { placeholder: "name",
      hyperlink: {
        url: profileLink
      }
    });
    slide.addText(employee.title || "No position", { placeholder: "position" });
    slide.addText(practiceMajor, { placeholder: "practice-major" });
    slide.addText(employee.location || "", { placeholder: "office" });

    slide.addText(formatStrengthsWithLink(prompts[10]?.response), {
      placeholder: "strengths"
    });

    slide.addText(formatBioWithLink(prompts[0]?.response), {
      placeholder: "bio"
    });

    slide.addText(formatCommaText(experiencedRoles), {
      placeholder: "role type"
    });

    slide.addText(formatSkillsWithLink(skills), {
      placeholder: "skills"
    });

    slide.addText(formatCertsWithLink(certifications), {
      placeholder: "certifications"
    });

    pptx.writeFile({
      fileName: `${prefix}_${employee.firstName}_${employee.lastName}_Bio_Slide_${today}`
    });
  };

  return (
    <Button
      icon="user"
      onClick={handleDownload}
      style={{ marginLeft: "20px", verticalAlign: "middle" }}
    >
      Generate Bio Slide
    </Button>
  );
};